// Base traits: each gene maps an allele value (0..ALLELE_MAX) to a stat
// modifier. The full trait registry defines the genome layout — index in
// `BASE_TRAITS` == gene index in the genome.
//
// Modifier types:
//   - 'mul'   : multiply base stat by `value` (e.g. speed 0.6x .. 1.6x)
//   - 'add'   : add `value` to base stat (e.g. +N to attackDamage)
//   - 'set'   : overwrite stat entirely with `value` (used sparingly)
//
// Modifiers from multiple traits combine deterministically: muls multiply,
// adds sum, sets last-wins (rare).

import type { Allele, Genome } from '../genome';
import { ALLELE_MAX } from '../genome';

export type StatKey =
  | 'speed'
  | 'maxHealth'
  | 'attackRange'
  | 'attackRate'
  | 'attackDamage'
  | 'projectileSpeed'
  | 'size'
  | 'mass'
  | 'lifespan'
  | 'maturationRate'
  | 'fertilityWindowStart'
  | 'fertilityWindowEnd'
  | 'cohesion'
  | 'separation'
  | 'alignment'
  | 'pickupAttraction'
  | 'xpGain'
  | 'regen'
  | 'armor'
  | 'crit'
  | 'evasion';

export type ModifierOp = 'mul' | 'add' | 'set';

export interface StatModifier {
  stat: StatKey;
  op: ModifierOp;
  value: number;
}

export interface BaseTrait {
  /** Stable id, also the gene name. */
  id: string;
  /** Player-facing label. */
  label: string;
  /** Which stat this trait expresses, for UI grouping. */
  stat: StatKey;
  /** Map an allele value (0..ALLELE_MAX) to a stat modifier. */
  modifier: (allele: Allele) => StatModifier;
}

/** Helper: linear map allele in [0, ALLELE_MAX] to value in [lo, hi]. */
function lerp(allele: Allele, lo: number, hi: number): number {
  return lo + (hi - lo) * (allele / ALLELE_MAX);
}

/** Helper: build a multiplicative trait (e.g. speed 0.6x..1.6x). */
function mulTrait(id: string, label: string, stat: StatKey, lo: number, hi: number): BaseTrait {
  return {
    id,
    label,
    stat,
    modifier: (a) => ({ stat, op: 'mul', value: lerp(a, lo, hi) }),
  };
}

/** Helper: build an additive trait (e.g. +0..+5 attackDamage). */
function addTrait(id: string, label: string, stat: StatKey, lo: number, hi: number): BaseTrait {
  return {
    id,
    label,
    stat,
    modifier: (a) => ({ stat, op: 'add', value: lerp(a, lo, hi) }),
  };
}

// The registry. Index in this array == gene index in the genome.
// Order is stable — appending is safe, reordering BREAKS save data.
export const BASE_TRAITS: readonly BaseTrait[] = [
  mulTrait('speed', 'Speed', 'speed', 0.6, 1.6),
  mulTrait('maxHealth', 'Vitality', 'maxHealth', 0.5, 2.0),
  mulTrait('attackRange', 'Reach', 'attackRange', 0.6, 1.8),
  mulTrait('attackRate', 'Tempo', 'attackRate', 0.6, 1.8),
  addTrait('attackDamage', 'Power', 'attackDamage', 0, 6),
  mulTrait('projectileSpeed', 'Velocity', 'projectileSpeed', 0.7, 1.6),
  mulTrait('size', 'Stature', 'size', 0.6, 1.5),
  mulTrait('mass', 'Density', 'mass', 0.5, 2.0),
  mulTrait('lifespan', 'Longevity', 'lifespan', 0.5, 1.8),
  mulTrait('maturationRate', 'Precocity', 'maturationRate', 0.6, 1.6),
  addTrait('fertilityWindowStart', 'Early Bloom', 'fertilityWindowStart', -0.1, 0.1),
  addTrait('fertilityWindowEnd', 'Late Bloom', 'fertilityWindowEnd', -0.1, 0.1),
  mulTrait('cohesion', 'Loyalty', 'cohesion', 0.3, 1.7),
  mulTrait('separation', 'Aloofness', 'separation', 0.3, 1.7),
  mulTrait('alignment', 'Synchrony', 'alignment', 0.3, 1.7),
  mulTrait('pickupAttraction', 'Magnetism', 'pickupAttraction', 0.5, 2.5),
  addTrait('xpGain', 'Sharpness', 'xpGain', 0, 0.6),
  addTrait('regen', 'Recovery', 'regen', 0, 1.5),
  addTrait('armor', 'Toughness', 'armor', 0, 4),
  addTrait('crit', 'Killer Instinct', 'crit', 0, 0.4),
  addTrait('evasion', 'Slipperiness', 'evasion', 0, 0.3),
];

/** Length of a genome = base traits + hidden genes. */
export function genomeLength(): number {
  return BASE_TRAITS.length + 4;
}

/** Look up a base trait by id. Throws if unknown. */
export function getTrait(id: string): BaseTrait {
  const t = BASE_TRAITS.find((x) => x.id === id);
  if (!t) throw new Error(`unknown base trait: ${id}`);
  return t;
}

/** Index of a base trait in the genome by id. -1 if unknown. */
export function traitIndex(id: string): number {
  return BASE_TRAITS.findIndex((x) => x.id === id);
}

// ---------------------------------------------------------------------------
// Stat resolution
// ---------------------------------------------------------------------------

export type StatSheet = Partial<Record<StatKey, number>>;

/** Base values (1x mul, 0 add) used as the starting point for stat resolution. */
export const BASE_STATS: Readonly<Record<StatKey, number>> = {
  speed: 100,
  maxHealth: 10,
  attackRange: 120,
  attackRate: 1.5,
  attackDamage: 1,
  projectileSpeed: 300,
  size: 1,
  mass: 1,
  lifespan: 60, // seconds
  maturationRate: 1,
  fertilityWindowStart: 0.25, // fraction of lifespan
  fertilityWindowEnd: 0.75,
  cohesion: 1,
  separation: 1,
  alignment: 1,
  pickupAttraction: 1,
  xpGain: 1,
  regen: 0,
  armor: 0,
  crit: 0,
  evasion: 0,
};

/** Resolve a genome to a complete StatSheet by applying all modifiers. */
export function resolveStats(genome: Genome): Record<StatKey, number> {
  const stats: Record<StatKey, number> = { ...BASE_STATS };
  for (let i = 0; i < BASE_TRAITS.length; i++) {
    const trait = BASE_TRAITS[i]!;
    const allele = genome[i];
    if (allele === undefined) continue;
    const mod = trait.modifier(allele);
    const current = stats[mod.stat];
    switch (mod.op) {
      case 'mul':
        stats[mod.stat] = current * mod.value;
        break;
      case 'add':
        stats[mod.stat] = current + mod.value;
        break;
      case 'set':
        stats[mod.stat] = mod.value;
        break;
    }
  }
  return stats;
}
